/**
 * Keyboard Shortcuts Help Modal
 * @module components/starnexus/common/KeyboardShortcutsModal
 *
 */

import React from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import { KeyboardShortcut, formatShortcutKey } from '../../../hooks/starnexus/useKeyboardShortcuts';

interface KeyboardShortcutsModalProps {
  visible: boolean;
  onDismiss: () => void;
  shortcuts: KeyboardShortcut[];
}

interface ShortcutItemProps {
  shortcut: KeyboardShortcut;
}

const ShortcutItem: React.FC<ShortcutItemProps> = ({ shortcut }) => {
  return (
    <Box>
      <SpaceBetween direction="horizontal" size="s" alignItems="center">
        <Box display="inline-block" padding={{ horizontal: 'xs', vertical: 'xxs' }} fontSize="body-s" fontWeight="bold" color="text-status-info">
          <code
            style={{
              backgroundColor: 'var(--color-background-input-default)',
              border: '1px solid var(--color-border-input-default)',
              borderRadius: '4px',
              padding: '2px 8px',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
          >
            {formatShortcutKey(shortcut)}
          </code>
        </Box>
        <Box color="text-body-secondary">{shortcut.description}</Box>
      </SpaceBetween>
    </Box>
  );
};

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ visible, onDismiss, shortcuts }) => {
  // Group shortcuts by category
  const navigationShortcuts = shortcuts.filter((s) => s.category === 'navigation');
  const actionShortcuts = shortcuts.filter((s) => s.category === 'actions');
  const generalShortcuts = shortcuts.filter((s) => s.category === 'general');

  return (
    <Modal visible={visible} onDismiss={onDismiss} header="Keyboard shortcuts" closeAriaLabel="Close keyboard shortcuts help" size="medium">
      <SpaceBetween size="l">
        <ColumnLayout columns={2} variant="text-grid">
          {/* Navigation shortcuts */}
          <SpaceBetween size="s">
            <Box variant="h4">Navigation</Box>
            <SpaceBetween size="xs">
              {navigationShortcuts.map((shortcut, index) => (
                <ShortcutItem key={index} shortcut={shortcut} />
              ))}
            </SpaceBetween>
          </SpaceBetween>

          {/* Action shortcuts */}
          <SpaceBetween size="s">
            <Box variant="h4">Actions</Box>
            <SpaceBetween size="xs">
              {actionShortcuts.map((shortcut, index) => (
                <ShortcutItem key={index} shortcut={shortcut} />
              ))}
            </SpaceBetween>
          </SpaceBetween>
        </ColumnLayout>

        {/* General shortcuts */}
        <SpaceBetween size="s">
          <Box variant="h4">General</Box>
          <SpaceBetween size="xs">
            {generalShortcuts.map((shortcut, index) => (
              <ShortcutItem key={index} shortcut={shortcut} />
            ))}
          </SpaceBetween>
        </SpaceBetween>

        <Box variant="small" color="text-body-secondary">
          Press <code>/</code> anywhere to show this help dialog.
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default KeyboardShortcutsModal;
